import { useCallback, useRef, useState, type KeyboardEvent } from "react";
import { MathContent } from "./math-content";

interface ChoiceProblemProps {
  questionHtml: string;
  choices: string[];
  className?: string;
  selectedAnswer?: string;
  onSelectAnswer?: (value: string) => void;
  disabled?: boolean;
  firstButtonRef?: (el: HTMLButtonElement | null) => void;
  /** Figures in the question open full screen. Never the choices: a tap there
      already picks an answer. */
  zoomableFigures?: boolean;
}

/** Detect whether a problem has choices (multiple-choice). */
export function isChoiceProblem(choices: string[] | null | undefined): boolean {
  return !!choices && choices.length > 0;
}

/**
 * Renders a question with clickable choice buttons below it.
 *
 * The question is rendered via MathContent (HTML+KaTeX).
 * Choices are rendered as styled pill buttons.
 */
export function ChoiceProblem({
  questionHtml,
  choices,
  className,
  selectedAnswer,
  onSelectAnswer,
  disabled,
  firstButtonRef,
  zoomableFigures,
}: ChoiceProblemProps) {
  const buttonsRef = useRef<(HTMLButtonElement | null)[]>([]);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  const handleSelect = useCallback(
    (value: string) => {
      if (!onSelectAnswer || disabled) return;
      // Toggle: clicking the same option deselects it
      if (value === selectedAnswer) {
        onSelectAnswer("");
      } else {
        onSelectAnswer(value);
      }
    },
    [onSelectAnswer, selectedAnswer, disabled]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
      if (disabled) return;
      const buttons = buttonsRef.current.filter(Boolean) as HTMLButtonElement[];
      if (buttons.length === 0) return;
      let next: number | null = null;
      switch (e.key) {
        case "ArrowRight":
        case "ArrowDown":
          next = (index + 1) % buttons.length;
          break;
        case "ArrowLeft":
        case "ArrowUp":
          next = (index - 1 + buttons.length) % buttons.length;
          break;
        case "Home":
          next = 0;
          break;
        case "End":
          next = buttons.length - 1;
          break;
      }
      if (next !== null) {
        e.preventDefault();
        buttons[next]?.focus();
      }
    },
    [disabled]
  );

  return (
    <div className={className}>
      <MathContent html={questionHtml} blankValue={selectedAnswer} zoomableFigures={zoomableFigures} />
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
          marginTop: "12px",
        }}
      >
        {choices.map((choice, index) => {
          const isSelected = choice === selectedAnswer;
          const isFocused = focusedIndex === index;
          const showFocusRing = isFocused && !isSelected;
          return (
            <button
              key={choice}
              ref={(el) => {
                buttonsRef.current[index] = el;
                if (index === 0) firstButtonRef?.(el);
              }}
              type="button"
              onClick={() => handleSelect(choice)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onFocus={() => setFocusedIndex(index)}
              onBlur={() => setFocusedIndex((i) => (i === index ? null : i))}
              disabled={disabled}
              style={{
                padding: "8px 16px",
                borderRadius: "8px",
                border: isSelected
                  ? "2px solid #3b82f6"
                  : showFocusRing
                    ? "2px dashed #f59e0b"
                    : "2px solid #e5e7eb",
                background: isSelected
                  ? "#eff6ff"
                  : showFocusRing
                    ? "#fffbeb"
                    : "#fff",
                color: showFocusRing ? "#92400e" : undefined,
                cursor: disabled ? "default" : "pointer",
                fontSize: "inherit",
                fontFamily: "inherit",
                minWidth: "48px",
                textAlign: "center",
                outline: "none",
                boxShadow: showFocusRing
                  ? "0 0 0 4px rgba(245, 158, 11, 0.25)"
                  : "none",
                transform: showFocusRing ? "translateY(-1px)" : "none",
                transition: "box-shadow 120ms, transform 120ms, border-color 120ms, background 120ms",
              }}
            >
              <MathContent html={choice} />
            </button>
          );
        })}
      </div>
    </div>
  );
}
